import React from "react";
import Dinero from "dinero.js";
import classNames from "classnames";
import $ from "./App.module.css";
import { VictoryPie, VictoryLabel } from "victory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useHistory } from "react-router-dom";
import {
  faColumns,
  faExclamationTriangle,
  faExpandAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { Map, Set } from "immutable";

export const calculateExpense = vs => {
  const lph = Number.parseFloat(vs.get("lossPerHour"));
  const hrs = Number.parseFloat(vs.get("hours"));
  const occ = Number.parseFloat(vs.get("occurrences"));

  return !Number.isFinite(lph) || !Number.isFinite(hrs) || !Number.isFinite(occ)
    ? Dinero({ amount: 0 })
    : Dinero({ amount: Math.round(lph * 100) | 0 })
        .multiply(hrs || 0)
        .multiply(occ || 0);
};

const flattenExpenses = exs => exs.valueSeq().flatMap(x => x.valueSeq());

const sum = coll => coll.reduce((acc, x) => acc + x, 0);

export const calculateExpenses = exs =>
  flattenExpenses(exs)
    .reduce((acc, v) => acc.add(calculateExpense(v)), Dinero({ amount: 0 }))
    .toFormat("0,0");

export const calculateHours = exs =>
  sum(
    flattenExpenses(exs)
      .map(x => Number.parseFloat(x.get("hours")))
      .filter(Number.isFinite),
  );

export const calculateParts = exs =>
  Set(exs.valueSeq().flatMap(x => x.keySeq())).size;

const lossByEquipment = xps =>
  xps.entrySeq().map(([k, v]) => {
    return Map({
      x: k,
      y: sum(v.valueSeq().map(x => calculateExpense(x).toUnit())),
    });
  });

const lossByPart = xps => {
  const equipmentParts = xps.valueSeq();
  const allParts = Set(equipmentParts.flatMap(parts => parts.keySeq()));
  return allParts.map(part => {
    return Map({
      x: part,
      y: sum(
        equipmentParts.map(parts =>
          calculateExpense(parts.get(part, Map())).toUnit(),
        ),
      ),
    });
  });
};

const downtimeByEquipment = xps =>
  xps.entrySeq().map(([equipment, parts]) => {
    const hours = sum(
      parts
        .valueSeq()
        .map(x => Number.parseFloat(x.get("hours", 0)))
        .filter(Number.isFinite),
    );
    return Map({ x: equipment, y: hours });
  });

const downtimeByPart = xps => {
  const equipmentParts = xps.valueSeq();
  const allParts = Set(equipmentParts.flatMap(parts => parts.keySeq()));
  return allParts.map(part => {
    const hours = sum(
      equipmentParts
        .map(parts => parseFloat(parts.getIn([part, "hours"])))
        .filter(Number.isFinite),
    );
    return Map({
      x: part,
      y: hours,
    });
  });
};

const AnalysisCard = ({ title, data }) => {
  const sum = data.reduce((acc, datum) => acc + datum.get("y"), 0);

  const transformText = ({ datum: { x, y } }) => {
    x = x || "undefined";
    if (y / sum < 0.02) {
      return "";
    } else if (x.length > 15) {
      return `${x.substring(0, 12).trim()}...`;
    } else {
      return x;
    }
  };

  return (
    <div className={classNames($.analysisCard, "animated", "fadeIn")}>
      <div className={$.analysisTitle}>{title}</div>
      <div className={$.analysisBody}>
        {data.size > 0 ? (
          <VictoryPie
            animate={{ duration: 200 }}
            height={300}
            colorScale={"warm"}
            data={data.toJS()}
            labelComponent={<VictoryLabel text={transformText} />}
            labelPosition={"centroid"}
            padAngle={1}
            innerRadius={50}
          />
        ) : (
          <div className={$.analysisMissing}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              style={{ marginRight: "5px" }}
            />
            Data required.
          </div>
        )}
      </div>
    </div>
  );
};

export const Analyses = ({ state }) => {
  const expenses = state.get("expenses");

  const isReady = expenses
    .valueSeq()
    .some(x => x.valueSeq().some(x => x.valueSeq().size > 0));

  return isReady ? (
    <div className={$.bladeAuto}>
      <Link to={`/data/${state.get("uuid")}/analyses`}>
        <div className={$.dashButton}>
          <FontAwesomeIcon icon={faExpandAlt} />
        </div>
      </Link>
      <AnalysisCard
        title="Loss by Equipment"
        data={lossByEquipment(expenses)}
      />
      <AnalysisCard title="Loss by Broken Part" data={lossByPart(expenses)} />{" "}
    </div>
  ) : (
    <div />
  );
};

export const FullAnalyses = ({ state }) => {
  const expenses = state.get("expenses");
  const history = useHistory();

  return (
    <div className={$.bladeAuto}>
      <div onClick={() => history.goBack()}>
        <div className={$.dashButton}>
          <FontAwesomeIcon icon={faColumns} />
        </div>
      </div>
      <AnalysisCard
        title="Loss by Equipment"
        data={lossByEquipment(expenses)}
      />
      <AnalysisCard title="Loss by Broken Part" data={lossByPart(expenses)} />
      <AnalysisCard
        title="Downtime by Equipment"
        data={downtimeByEquipment(expenses)}
      />
      <AnalysisCard
        title="Downtime by Broken Part"
        data={downtimeByPart(expenses)}
      />
    </div>
  );
};
